import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, FileText } from "lucide-react";
import api from "@/lib/api";
import { blobFromSplat } from "@/lib/viewer";
import { isPdfDoc } from "@/lib/isPdf";
import DocumentDetailPanel from "@/components/documents/DocumentDetailPanel";

const REFRESH_LEAD_MS = 60 * 1000; // refetch ~1 min before the SAS expires
const MIN_REFRESH_MS = 10 * 1000; // never spin on an already-expired stamp
const FALLBACK_REFRESH_MS = 13 * 60 * 1000; // if expires_at is unparsable


// Full-page document viewer: browser PDF viewer in an iframe on the left,
// editable detail panel on the right. SAS URLs live in component state only.
export default function DocumentViewerPage() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const blob = blobFromSplat(params["*"]);

  // status: loading | ready | notfound | error
  const [status, setStatus] = useState("loading");
  const [item, setItem] = useState(null);
  const timerRef = useRef(null);

  const goBack = useCallback(() => {
    if (location.key === "default") navigate("/documents");
    else navigate(-1);
  }, [location.key, navigate]);

  const fetchUrls = useCallback(async () => {
    const res = await api.post("/documents/preview-urls", { blob_paths: [blob] });
    const urls = res?.data?.urls || [];
    return urls.find((u) => u.blob_path === blob) || urls[0] || null;
  }, [blob]);

  useEffect(() => {
    let alive = true;
    setStatus("loading");
    setItem(null);

    const load = async () => {
      let found;
      try {
        found = await fetchUrls();
      } catch {
        found = await fetchUrls(); // one retry; a second failure throws
      }
      return found;
    };

    const schedule = (it) => {
      clearTimeout(timerRef.current);
      const exp = Date.parse(it?.expires_at);
      const delay = Number.isNaN(exp)
        ? FALLBACK_REFRESH_MS
        : Math.max(exp - Date.now() - REFRESH_LEAD_MS, MIN_REFRESH_MS);
      timerRef.current = setTimeout(async () => {
        try {
          const next = await load();
          if (!alive || !next) return;
          setItem(next);
          schedule(next);
        } catch {
          if (alive) setStatus("error");
        }
      }, delay);
    };

    (async () => {
      try {
        const first = await load();
        if (!alive) return;
        if (!first) {
          setStatus("notfound");
          return;
        }
        setItem(first);
        setStatus("ready");
        schedule(first);
      } catch {
        if (alive) setStatus("error");
      }
    })();

    // Clears the "new" badge; failures are irrelevant to viewing.
    Promise.resolve(api.post(`/documents/${encodeURIComponent(blob)}/open`)).catch(() => {});

    return () => {
      alive = false;
      clearTimeout(timerRef.current);
    };
  }, [blob, fetchUrls]);

  // Esc = Back, unless the user is typing / a popover is open.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      const t = e.target;
      const tag = t?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t?.isContentEditable) return;
      if (
        document.querySelector(
          '[data-radix-popper-content-wrapper], [role="listbox"], [role="menu"], [role="dialog"]'
        )
      ) {
        return;
      }
      goBack();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goBack]);

  const filename = item?.filename || blob.split("/").pop();
  const pdf = isPdfDoc(item?.filename, blob);

  let viewer;
  if (status === "loading") {
    viewer = <div className="h-full bg-gray-100 animate-pulse" data-testid="viewer-loading" />;
  } else if (status === "notfound") {
    viewer = <Message>Document not found.</Message>;
  } else if (status === "error") {
    viewer = <Message>Couldn&apos;t load this document. Reload to try again.</Message>;
  } else if (!pdf) {
    viewer = (
      <div className="h-full flex flex-col items-center justify-center gap-3 text-gray-500">
        <FileText className="w-12 h-12" aria-hidden="true" />
        <p className="text-sm">Preview not available</p>
        <a
          href={item.download_url}
          className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border bg-white hover:bg-gray-50"
        >
          <Download className="w-4 h-4" /> Download
        </a>
      </div>
    );
  } else {
    viewer = (
      <iframe title={filename} src={item.url} className="w-full h-full border-0 bg-white" />
    );
  }

  return (
    <div className="h-screen flex flex-col bg-white text-gray-900">
      <header className="flex items-center gap-3 px-4 py-2 border-b bg-white">
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-1.5 text-sm px-2 py-1 rounded-md hover:bg-gray-100"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h1 className="flex-1 min-w-0 truncate text-sm font-medium" dir="auto">
          {status === "ready" ? filename : ""}
        </h1>
        {status === "ready" && item?.download_url && (
          <a
            href={item.download_url}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border hover:bg-gray-50"
          >
            <Download className="w-4 h-4" /> Download
          </a>
        )}
      </header>
      <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-auto lg:overflow-hidden">
        <div className="h-[70vh] lg:h-auto lg:flex-1 min-w-0 border-b lg:border-b-0 lg:border-r bg-gray-50">
          {viewer}
        </div>
        {status !== "notfound" && (
          <aside className="lg:w-[380px] lg:flex-shrink-0 flex flex-col min-h-0 lg:overflow-hidden">
            <DocumentDetailPanel blobName={blob} />
          </aside>
        )}
      </div>
    </div>
  );
}

function Message({ children }) {
  return (
    <div className="h-full flex items-center justify-center text-sm text-gray-500 px-4 text-center">
      {children}
    </div>
  );
}
